/**
 * GraphQL Client (URQL)
 *
 * URQL client configuration with:
 * - Normalized cache (graphcache) for automatic updates after mutations
 * - Dynamic auth headers (Telegram initData or JWT)
 * - Idempotency for mutations (prevents double purchases)
 * - Circuit breaker for cascading failure protection
 * - Global error handling
 * - Cross-tab 401 coordination via BroadcastChannel
 *
 * CSRF Protection:
 * This client uses Bearer token authentication (not cookies), which provides
 * inherent CSRF protection because:
 * 1. Tokens are stored in localStorage (same-origin only)
 * 2. Cross-origin requests cannot read localStorage
 * 3. Custom headers (Authorization, X-Requested-With) trigger CORS preflight
 * 4. API rejects requests without valid auth headers
 *
 * Additionally, we add X-Requested-With header as defense-in-depth.
 */

import { createClient, fetchExchange, mapExchange } from "@urql/core";
import type { Operation, CombinedError, Exchange, OperationResult } from "@urql/core";
import { cacheExchange } from "@urql/exchange-graphcache";
import { pipe, filter, map, mergeMap, fromPromise, fromValue } from "wonka";
import { print } from "graphql";
import { config } from "../config";
import { getInitData } from "../lib/telegram";
import {
  getStoredTokens,
  getStoredTokensAsync,
  isTokenExpired,
  clearTokens,
  saveTokens,
} from "../lib/storage";
import { errorTracking } from "../lib/error-tracking";
import { circuitBreakerExchange } from "./circuit-breaker-exchange";
import { createLogger } from "../lib/logger";
import { useAuthStore } from "../stores/auth-store";

// =============================================================================
// CONSTANTS
// =============================================================================

const GRAPHQL_URL = `${config.apiUrl}/graphql`;
const IDEMPOTENCY_HEADER = "X-Idempotency-Key";

const apiLog = createLogger("api");

/**
 * Operations that don't require authentication.
 * These are public queries that should work without any auth headers.
 */
const PUBLIC_OPERATIONS = [
  // Auth operations
  "Authenticate",
  "AuthenticateWithInitData",
  "AuthenticateWithWidget",
  "RefreshToken",
  "LoginWithEmail",
  "RegisterWithEmail",
  // Public queries
  "AnnouncementEvents",
  "Events",
  "Event",
  "ScheduleCategories",
  // Public pages content
  "WelcomeContent",
  // Academy public queries
  "AcademyCourses",
];

// =============================================================================
// AUTH HEADERS
// =============================================================================

/**
 * Check if we have valid auth credentials.
 * 
 * Uses multiple sources for reliability:
 * 1. Telegram initData (Mini App context)
 * 2. Auth store (in-memory, synced with AuthContext)
 * 3. Stored tokens (localStorage fallback for legacy/plaintext tokens)
 */
function hasValidAuth(): boolean {
  // Check Telegram initData first (Mini App)
  const initData = getInitData();
  if (initData) {
    return true;
  }

  // Check auth store (in-memory state, works with obfuscated tokens)
  if (useAuthStore.getState().isAuthenticated) {
    return true;
  }

  // Fallback: check stored tokens (for legacy plaintext tokens)
  const tokens = getStoredTokens();
  return !!(tokens && !isTokenExpired(tokens.expiresAt));
}

/**
 * Get operation name from GraphQL operation.
 */
function getOperationName(operation: Operation): string | null {
  const definition = operation.query.definitions[0];
  if (definition && "name" in definition && definition.name) {
    return definition.name.value;
  }
  return null;
}

/**
 * Get authorization headers dynamically for each request.
 * Priority: Telegram initData > JWT Bearer token
 *
 * Includes X-Requested-With header for CSRF defense-in-depth:
 * - This custom header triggers CORS preflight for cross-origin requests
 * - Browsers won't send it from cross-origin forms/scripts without CORS approval
 * - Combined with Bearer token auth, provides strong CSRF protection
 */
function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    // CSRF defense-in-depth: custom header triggers CORS preflight
    "X-Requested-With": "XMLHttpRequest",
  };

  // Use Telegram initData if available (primary auth in Mini App)
  const initData = getInitData();
  if (initData) {
    headers["x-telegram-init-data"] = initData;
    return headers;
  }

  // Fallback to JWT token for PWA mode
  const tokens = getStoredTokens();
  if (tokens && !isTokenExpired(tokens.expiresAt)) {
    headers["Authorization"] = `Bearer ${tokens.accessToken}`;
  }

  return headers;
}

// =============================================================================
// EXCHANGES
// =============================================================================

/**
 * Auth guard exchange - blocks requests without valid auth.
 * Public operations (authenticate, etc.) are allowed through.
 */
const authGuardExchange: Exchange =
  ({ forward }) =>
  (ops$) => {
    return pipe(
      ops$,
      filter((operation) => {
        // Always allow teardowns
        if (operation.kind === "teardown") {
          return true;
        }

        // Allow public operations
        const opName = getOperationName(operation);
        if (opName && PUBLIC_OPERATIONS.includes(opName)) {
          return true;
        }

        // Check for valid auth
        if (!hasValidAuth()) {
          apiLog.warn("Blocked request - no valid auth", { operation: opName });
          return false;
        }

        return true;
      }),
      forward
    );
  };

/**
 * Idempotency exchange - adds unique key to all mutations.
 * Prevents duplicate purchases when user clicks multiple times.
 */
const idempotencyExchange: Exchange =
  ({ forward }) =>
  (ops$) => {
    return pipe(
      ops$,
      map((operation) => {
        // Only add idempotency key to mutations
        if (operation.kind !== "mutation") {
          return operation;
        }

        const operationName = getOperationName(operation) ?? "Mutation";
        const idempotencyKey = `${operationName}:${crypto.randomUUID()}`;

        // Merge idempotency header into fetch options
        const existingFetchOptions = operation.context.fetchOptions;

        const newFetchOptions =
          typeof existingFetchOptions === "function"
            ? () => {
                const opts = existingFetchOptions();
                return {
                  ...opts,
                  headers: {
                    ...(opts?.headers || {}),
                    [IDEMPOTENCY_HEADER]: idempotencyKey,
                  },
                };
              }
            : {
                ...(existingFetchOptions as RequestInit | undefined),
                headers: {
                  ...((existingFetchOptions as RequestInit | undefined)
                    ?.headers || {}),
                  [IDEMPOTENCY_HEADER]: idempotencyKey,
                },
              };

        return {
          ...operation,
          context: {
            ...operation.context,
            fetchOptions: newFetchOptions,
          },
        };
      }),
      forward
    );
  };

// =============================================================================
// AUTH REFRESH EXCHANGE
// =============================================================================

/**
 * Mutex for token refresh to prevent concurrent refresh attempts.
 * Only one refresh can happen at a time - other requests wait for it.
 */
let refreshPromise: Promise<boolean> | null = null;

/**
 * GraphQL mutation for token refresh.
 */
const REFRESH_TOKEN_MUTATION = `
  mutation RefreshToken($refreshToken: String!) {
    refreshToken(refreshToken: $refreshToken) {
      accessToken
      refreshToken
      expiresAt
    }
  }
`;

/**
 * Check if error is an authentication error (401/UNAUTHENTICATED).
 */
function isAuthError(error: CombinedError): boolean {
  return (
    error.graphQLErrors?.some(
      (e) =>
        e.extensions?.code === "UNAUTHENTICATED" ||
        e.message?.includes("Authentication required")
    ) || error.response?.status === 401
  );
}

/**
 * Perform token refresh with mutex protection.
 * Returns true if refresh succeeded, false otherwise.
 */
async function performTokenRefresh(): Promise<boolean> {
  // If refresh is already in progress, wait for it
  if (refreshPromise) {
    apiLog.debug("Token refresh already in progress, waiting...");
    return refreshPromise;
  }

  // Start new refresh
  refreshPromise = (async () => {
    try {
      apiLog.debug("Starting token refresh...");

      // Get refresh token (try async first for obfuscated tokens)
      const tokens = await getStoredTokensAsync();
      if (!tokens?.refreshToken) {
        apiLog.debug("No refresh token available");
        return false;
      }

      // Call refresh mutation directly via fetch (bypass URQL to avoid loops)
      const response = await fetch(`${config.apiUrl}/graphql`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          query: REFRESH_TOKEN_MUTATION,
          variables: { refreshToken: tokens.refreshToken },
        }),
      });

      if (!response.ok) {
        apiLog.debug("Token refresh HTTP error:", response.status);
        return false;
      }

      const result = await response.json();

      if (result.errors?.length > 0) {
        apiLog.debug("Token refresh GraphQL error:", result.errors);
        return false;
      }

      const newTokens = result.data?.refreshToken;
      if (!newTokens?.accessToken) {
        apiLog.debug("Token refresh returned no tokens");
        return false;
      }

      // Save new tokens
      await saveTokens(newTokens);

      apiLog.info("Token refresh successful");
      return true;
    } catch (err) {
      apiLog.error("Token refresh failed:", err);
      return false;
    } finally {
      // Clear mutex
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Auth refresh exchange - intercepts 401 errors and attempts token refresh.
 *
 * Flow:
 * 1. Forward request to downstream exchanges
 * 2. If response has 401/UNAUTHENTICATED error:
 *    a. Attempt token refresh (with mutex to prevent concurrent refreshes)
 *    b. If refresh succeeds, retry the original request
 *    c. If refresh fails, clear tokens and pass error through
 * 3. Non-auth errors pass through unchanged
 *
 * This exchange should be placed BEFORE mapExchange to intercept errors
 * before they trigger logout in handleError.
 */
const authRefreshExchange: Exchange =
  ({ forward }) =>
  (ops$) => {
    // Track operations that are currently being retried (to prevent infinite loops)
    const retriedOperations = new Set<number>();

    return pipe(
      ops$,
      forward,
      mergeMap((result: OperationResult) => {
        const operation = result.operation;
        const opName = getOperationName(operation);

        // Skip if no auth error
        if (!result.error || !isAuthError(result.error)) {
          return fromValue(result);
        }

        // Skip for public operations (shouldn't get 401, but just in case)
        if (opName && PUBLIC_OPERATIONS.includes(opName)) {
          return fromValue(result);
        }

        // Skip for Telegram Mini App (uses initData, not JWT)
        if (isInTelegram()) {
          return fromValue(result);
        }

        // Skip if we already retried this operation (prevent infinite loop)
        if (retriedOperations.has(operation.key)) {
          apiLog.debug("Already retried operation, not retrying again", { operation: opName });
          return fromValue(result);
        }

        // Got 401 error - attempt refresh and retry
        apiLog.debug("Got 401 error, attempting token refresh...", { operation: opName });

        return fromPromise(
          (async (): Promise<OperationResult> => {
            const refreshed = await performTokenRefresh();

            if (!refreshed) {
              // Refresh failed - clear tokens and logout
              apiLog.debug("Token refresh failed, clearing tokens");
              clearTokens();
              useAuthStore.getState().clearAuth();
              broadcast401Handled();
              return result; // Return original error
            }

            // Refresh succeeded - retry the original operation
            apiLog.debug("Token refresh succeeded, retrying operation", { operation: opName });

            // Mark as retried to prevent infinite loops
            retriedOperations.add(operation.key);

            // Re-execute the operation with fresh tokens via direct fetch
            // (using the exchange again could cause loops with stale headers)
            try {
              const tokens = getStoredTokens();
              if (!tokens) {
                return result;
              }

              const retryResponse = await fetch(`${config.apiUrl}/graphql`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "X-Requested-With": "XMLHttpRequest",
                  Authorization: `Bearer ${tokens.accessToken}`,
                },
                body: JSON.stringify({
                  query: print(operation.query),
                  variables: operation.variables,
                  operationName: opName,
                }),
              });

              const retryResult = await retryResponse.json();

              // Clean up retry tracking
              retriedOperations.delete(operation.key);

              // Return as OperationResult
              return {
                ...result,
                data: retryResult.data,
                error: retryResult.errors?.length
                  ? {
                      ...result.error!,
                      graphQLErrors: retryResult.errors,
                    }
                  : undefined,
              };
            } catch (retryErr) {
              apiLog.error("Retry after refresh failed:", retryErr);
              retriedOperations.delete(operation.key);
              return result;
            }
          })()
        );
      })
    );
  };

// =============================================================================
// ERROR HANDLING
// =============================================================================

type ErrorHandler = (error: CombinedError, operation: Operation) => void;

let globalErrorHandler: ErrorHandler | null = null;

/**
 * Set global error handler for GraphQL errors.
 */
export function setGlobalErrorHandler(handler: ErrorHandler | null): void {
  globalErrorHandler = handler;
}

/**
 * Check if running in Telegram Mini App context.
 */
function isInTelegram(): boolean {
  return !!getInitData();
}

// =============================================================================
// 401 HANDLING WITH CROSS-TAB COORDINATION
// =============================================================================

let auth401Channel: BroadcastChannel | null = null;

/**
 * Initialize cross-tab 401 coordination channel.
 * Call this once at app startup.
 */
function initAuth401Channel(): void {
  if (auth401Channel) return;

  try {
    auth401Channel = new BroadcastChannel("one-church:auth-401");

    // Listen for 401 events from other tabs
    auth401Channel.onmessage = (event) => {
      if (event.data?.type === "auth-401-handled") {
        apiLog.debug("Received 401 handled event from another tab");
        // Another tab already handled the 401 - just reset our client
        resetUrqlClient();
        useAuthStore.getState().clearAuth();
      }
    };
  } catch {
    // BroadcastChannel not supported (older browsers, some WebViews)
    apiLog.warn("BroadcastChannel not supported for cross-tab 401 coordination");
  }
}

// Initialize channel on module load
initAuth401Channel();

/**
 * Broadcast that we handled a 401 error (tokens cleared, user logged out).
 * Other tabs will reset their clients when they receive this.
 */
function broadcast401Handled(): void {
  try {
    auth401Channel?.postMessage({ type: "auth-401-handled", timestamp: Date.now() });
  } catch {
    // Ignore errors (channel might be closed)
  }
}

/**
 * Handle GraphQL/network errors.
 *
 * NOTE: 401/UNAUTHENTICATED errors are handled by authRefreshExchange which:
 * 1. Attempts token refresh before treating it as a real auth error
 * 2. Only clears tokens if refresh fails
 * 3. Broadcasts to other tabs on logout
 *
 * This handler only logs and reports non-auth errors to Sentry.
 */
function handleError(error: CombinedError, operation: Operation): void {
  // Report to Sentry
  errorTracking.captureError(error, {
    tags: {
      errorType: "graphql",
      operation: getOperationName(operation) ?? "unknown",
    },
    extra: {
      graphQLErrors: error.graphQLErrors,
      networkError: error.networkError?.message,
    },
  });

  // Call global error handler
  if (globalErrorHandler) {
    globalErrorHandler(error, operation);
  }

  apiLog.error("GraphQL Error", {
    operation: getOperationName(operation),
    graphQLErrors: error.graphQLErrors,
    networkError: error.networkError,
  });
}

// =============================================================================
// CACHE CONFIGURATION
// =============================================================================

/**
 * Build the cache exchange with entity keys and invalidation rules.
 */
function buildCacheExchange(): Exchange {
  return cacheExchange({
    // Define keys for entity identification
    keys: {
      Account: (data) => String(data.id),
      Event: (data) => String(data.id),
      TicketType: (data) => String(data.id),
      Order: (data) => String(data.id),
      Ticket: (data) => String(data.id),
      FeeConfiguration: (data) => String(data.id),
      // Types without IDs - don't cache as entities
      AuthTokens: () => null,
      FeeBreakdown: () => null,
      TicketTypePrice: () => null,
      OrderPricing: () => null,
      OrderPricingFee: () => null,
      ConversationMessage: () => null,
    },
    // Invalidate queries after mutations
    updates: {
      Mutation: {
        // Orders
        createOrder: (_result, _args, cache) => {
          cache.invalidate("Query", "orders");
          cache.invalidate("Query", "order");
        },
        proceedToPayment: (_result, _args, cache) => {
          cache.invalidate("Query", "order");
          cache.invalidate("Query", "orders");
        },
        cancelOrder: (_result, _args, cache) => {
          cache.invalidate("Query", "order");
          cache.invalidate("Query", "orders");
        },
        // Tickets
        issueTicketsForCash: (_result, _args, cache) => {
          cache.invalidate("Query", "adminOrders");
          cache.invalidate("Query", "event");
        },
        validateTicket: (_result, _args, cache) => {
          cache.invalidate("Query", "ticket");
          cache.invalidate("Query", "adminTickets");
        },
        // Events (admin)
        createEvent: (_result, _args, cache) => {
          cache.invalidate("Query", "events");
          cache.invalidate("Query", "adminEvents");
        },
        updateEvent: (_result, _args, cache) => {
          cache.invalidate("Query", "events");
          cache.invalidate("Query", "event");
          cache.invalidate("Query", "adminEvents");
        },
        deleteEvent: (_result, _args, cache) => {
          cache.invalidate("Query", "events");
          cache.invalidate("Query", "adminEvents");
        },
        duplicateEvent: (_result, _args, cache) => {
          cache.invalidate("Query", "events");
          cache.invalidate("Query", "adminEvents");
          cache.invalidate("Query", "adminEvent");
        },
        // Ticket types
        createTicketType: (_result, _args, cache) => {
          cache.invalidate("Query", "event");
          cache.invalidate("Query", "adminEvent");
          cache.invalidate("Query", "adminEvents");
        },
        updateTicketType: (_result, _args, cache) => {
          cache.invalidate("Query", "event");
          cache.invalidate("Query", "adminEvent");
          cache.invalidate("Query", "adminEvents");
        },
        deleteTicketType: (_result, _args, cache) => {
          cache.invalidate("Query", "event");
          cache.invalidate("Query", "adminEvent");
          cache.invalidate("Query", "adminEvents");
        },
        // Event organizers
        addEventOrganizer: (_result, _args, cache) => {
          cache.invalidate("Query", "event");
          cache.invalidate("Query", "adminEvent");
        },
        removeEventOrganizer: (_result, _args, cache) => {
          cache.invalidate("Query", "event");
          cache.invalidate("Query", "adminEvent");
        },
        // RSVP
        confirmEventRsvp: (_result, _args, cache) => {
          cache.invalidate("Query", "event");
          cache.invalidate("Query", "orders");
          cache.invalidate("Query", "adminEventPendingRsvps");
        },
        // Ticket generation
        generateTickets: (_result, _args, cache) => {
          cache.invalidate("Query", "adminOrders");
          cache.invalidate("Query", "adminOrder");
          cache.invalidate("Query", "order");
          cache.invalidate("Query", "adminTickets");
          cache.invalidate("Query", "tickets");
        },
        resendTickets: (_result, _args, cache) => {
          cache.invalidate("Query", "adminOrders");
          cache.invalidate("Query", "adminOrder");
          cache.invalidate("Query", "order");
          cache.invalidate("Query", "tickets");
        },
        // Contact/Messages
        contactManager: (_result, _args, cache) => {
          cache.invalidate("Query", "order");
        },
        adminSendOrderMessage: (_result, _args, cache) => {
          cache.invalidate("Query", "adminUserConversation");
          cache.invalidate("Query", "adminOrdersByAccount");
        },
      },
    },
  });
}

// =============================================================================
// CLIENT FACTORY
// =============================================================================

/**
 * Create URQL GraphQL client with all exchanges.
 *
 * Exchange order is critical:
 * 1. authGuardExchange - blocks unauthenticated requests early
 * 2. idempotencyExchange - adds idempotency keys to mutations
 * 3. cacheExchange - normalized cache
 * 4. circuitBreakerExchange - fail fast on repeated failures
 * 5. authRefreshExchange - intercepts 401, refreshes tokens, retries requests
 * 6. mapExchange - global error handling (AFTER refresh attempts)
 * 7. fetchExchange - actual network request (always last)
 *
 * authRefreshExchange MUST be:
 * - AFTER circuitBreakerExchange (so circuit breaker tracks failures)
 * - BEFORE mapExchange (so it can intercept 401 before logout)
 */
export function createUrqlClient() {
  return createClient({
    url: GRAPHQL_URL,
    exchanges: [
      authGuardExchange,
      idempotencyExchange,
      buildCacheExchange(),
      circuitBreakerExchange,
      authRefreshExchange,
      mapExchange({
        onError: handleError,
      }),
      fetchExchange,
    ],
    // Dynamic fetch options - called on EVERY request
    fetchOptions: () => {
      return { headers: getAuthHeaders() };
    },
  });
}

// =============================================================================
// SINGLETON CLIENT
// =============================================================================

let urqlClient: ReturnType<typeof createUrqlClient> | null = null;

/**
 * Get or create the URQL client singleton.
 */
export function getUrqlClient() {
  if (!urqlClient) {
    urqlClient = createUrqlClient();
  }
  return urqlClient;
}

/**
 * Reset the client to clear ALL cached data.
 * Call this on logout or auth errors.
 */
export function resetUrqlClient(): void {
  urqlClient = null;
}
